// Global Instructions Rule Applied!
//
// Configuration-screen HTTP calls for the GLOBAL accounting settings every
// fund inherits: the GL account catalogue, the global accounting-item ->
// account default mapping, and chart templates. The PER-FUND chart lives on
// the fund form and is served by fundsService.
//
// Write endpoints answer 422 with the guard's own wording (duplicate account
// code, a global mapping move onto an account some fund has deactivated, a
// template item map outside its own account set). That message is shown
// verbatim, so errorMessage prefers the body's `error` over the status line.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  GlAccountAdminRow,
  GlItemDefaultRow,
  ChartTemplate,
  validateGlAccountAdminRows,
  validateGlItemDefaultRows
} from '../../../state_manager/data_models/fund_chart_of_accounts.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.error || body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

export interface AccountingWriteResponse {
  success: boolean
  data?: { status_message?: string } & Record<string, unknown>
  error?: string
}

export interface GlAccountAdminResponse {
  accounts: GlAccountAdminRow[]
  accountTypes: Array<{ value: string; label: string }>
  items: GlItemDefaultRow[]
  error?: string
}

export const fetchGlAccountAdmin = async (
  accessToken: string | null = null
): Promise<GlAccountAdminResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.GL_ACCOUNTS_ADMIN),
      { method: API_ROUTES.CONFIG.GL_ACCOUNTS_ADMIN.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch GL accounts'))
    const json = (await response.json()) as {
      data?: { accounts?: unknown[]; account_types?: Array<{ value: string; label: string }>; items?: unknown[] }
    }
    return {
      accounts: validateGlAccountAdminRows(json.data?.accounts ?? []),
      accountTypes: json.data?.account_types ?? [],
      items: validateGlItemDefaultRows(json.data?.items ?? [])
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching GL account admin:', error)
    return {
      accounts: [],
      accountTypes: [],
      items: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

const postJson = async (
  route: { path: string; method: string },
  body: unknown,
  accessToken: string | null,
  fallback: string,
  pathParams: Record<string, string> = {}
): Promise<AccountingWriteResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route as any, pathParams), {
      method: route.method,
      headers: authHeaders(accessToken),
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, fallback) }
    const json = (await response.json()) as { data?: Record<string, unknown> }
    return { success: true, data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(fallback, error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveGlAccount = (
  payload: { id?: string; account_code?: string | null; account_name: string; account_type: string },
  accessToken: string | null = null
) => postJson(API_ROUTES.CONFIG.GL_ACCOUNT_SAVE, { data: payload }, accessToken, 'Failed to save the GL account')

export const setGlobalItemMapping = (
  itemId: string,
  glAccountId: string,
  accessToken: string | null = null
) =>
  postJson(
    API_ROUTES.CONFIG.GL_ITEM_MAPPING_SET,
    { data: { item_id: itemId, gl_account_id: glAccountId } },
    accessToken,
    'Failed to set the default mapping'
  )

export const fetchChartTemplates = async (
  accessToken: string | null = null
): Promise<{ data: ChartTemplate[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CHART_TEMPLATES_LIST),
      { method: API_ROUTES.CONFIG.CHART_TEMPLATES_LIST.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch chart templates'))
    const json = (await response.json()) as { data?: ChartTemplate[] }
    return { data: json.data ?? [] }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching chart templates:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveChartTemplate = (
  payload: {
    id?: string
    name: string
    description?: string | null
    accounts?: string[]
    item_maps?: Array<{ account_item_id: string; gl_account_id: string }>
  },
  accessToken: string | null = null
) => postJson(API_ROUTES.CONFIG.CHART_TEMPLATE_SAVE, { data: payload }, accessToken, 'Failed to save the chart template')

export const deleteChartTemplate = (templateId: string, accessToken: string | null = null) =>
  postJson(
    API_ROUTES.CONFIG.CHART_TEMPLATE_DELETE,
    undefined,
    accessToken,
    'Failed to delete the chart template',
    { templateId }
  )
